import { useState, useEffect, useRef } from 'react';
import { messaging, getToken, onMessage } from '../firebase';
import { Capacitor } from '@capacitor/core';
import { trackShowFavorited, trackShowUnfavorited } from '../analytics';

export interface NotificationSettings {
  enabled: boolean;
  showScheduleReminders: boolean;
  showNewShowAlerts: boolean;
  favoriteShows: string[];
  reminderTime: number;
}

export interface ShowDetails {
  artist: string;
  showName: string;
}

// Helper to send preferences and FCM token to backend
const sendPreferencesToBackend = async (fcmToken: string, preferences: NotificationSettings) => {
  console.log('[DEBUG] sendPreferencesToBackend called with:', {
    hasToken: !!fcmToken,
    preferences
  });

  const API_URL = import.meta.env.DEV 
    ? 'http://localhost:3001/api/save-preferences'
    : 'http://localhost:3001/api/save-preferences';

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin
      },
      body: JSON.stringify({ fcmToken, preferences }),
    });

    console.log('[DEBUG] Backend response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('[DEBUG] Backend error:', errorData);
      throw new Error(`Failed to save preferences: ${errorData.error || response.statusText}`);
    }

    const data = await response.json();
    console.log('[DEBUG] Backend success response:', data);
    return true;
  } catch (error) {
    console.error('[DEBUG] Error saving preferences:', error);
    return false;
  }
};

// Call this when the user enables notifications
export async function subscribeUserToPush(preferences?: NotificationSettings) {
  console.log('[DEBUG] subscribeUserToPush called with preferences:', preferences);
  
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[DEBUG] Push notifications not supported in this browser.');
    alert('Push notifications are not supported on this device.');
    return false;
  }
  
  // Check for Safari iOS
  if (isSafariIOS()) {
    // Only use local notifications, do not attempt FCM
    alert('On iOS, you will only receive notifications while the app is open. Push notifications are not supported on iOS.');
    // Request permission for local notifications
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notifications permission not granted. Please try again.');
    }
    // Optionally, you could schedule a local notification here as a test
    if ('Notification' in window) {
      new Notification('Notifications enabled!', {
        body: 'You will receive reminders while the app is open.',
        icon: '/src/logo.png',
      });
    }
    return true;
  }
  
  if (!('serviceWorker' in navigator)) {
    console.log('[DEBUG] Service Worker not supported');
    throw new Error('Service Worker not supported in this browser');
  }
  
  try {
    console.log('[DEBUG] Requesting notification permission...');
    const permission = await Notification.requestPermission();
    console.log('[DEBUG] Notification permission result:', permission);
    
    if (permission !== 'granted') {
      console.log('[DEBUG] Permission not granted:', permission);
      if (permission === 'denied') {
        const browserInstructions = getBrowserInstructions();
        throw new Error('Notifications permission denied. ' + browserInstructions);
      } else {
        throw new Error('Notifications permission not granted. Please try again.');
      }
    }

    // Check if we're on Android and handle FCM token issues
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    // Get FCM token
    console.log('[DEBUG] Getting FCM token...');
    const vapidKey = 'BFxTvVjgQXtdbPcF2SoD1ycpGx4fx-dcVHRfutW-17x98Kw4rFdLLbiTPBUSZ0UukpxbF5mBenMF9F2_WBtXo';
    
    let fcmToken;
    try {
      fcmToken = await getToken(messaging, {
        vapidKey: vapidKey
      });
    } catch (tokenError: any) {
      console.error('[DEBUG] Error getting FCM token:', tokenError);
      
      // Check if it's the atob/base64 error (common on Android)
      if (tokenError.message && (tokenError.message.includes('atob') || tokenError.message.includes('base64'))) {
        console.warn('[DEBUG] Base64/atob error detected on Android, enabling local notifications only');
        
        // Show success message for local notifications
        alert('Notifications enabled! You will receive notifications while the app is open. Push notifications may not work on this device due to browser limitations.');
        
        // Show a test notification
        if ('Notification' in window) {
          new Notification('Notifications enabled!', {
            body: 'You will receive notifications while the app is open.',
            icon: '/src/logo.png',
          });
        }
        return true;
      }
      
      // For other FCM errors
      if (isAndroid) {
        console.warn('[DEBUG] FCM error on Android, falling back to local notifications');
        alert('Notifications enabled! You will receive notifications while the app is open. Full push notifications may not work on this device.');
        
        if ('Notification' in window) {
          new Notification('Notifications enabled!', {
            body: 'You will receive notifications while the app is open.',
            icon: '/src/logo.png',
          });
        }
        return true;
      }
      
      throw new Error(`Failed to get FCM token: ${tokenError.message}`);
    }

    if (!fcmToken) {
      // If no FCM token but we're on Android, still allow local notifications
      if (isAndroid) {
        console.warn('[DEBUG] No FCM token on Android, enabling local notifications only');
        alert('Notifications enabled! You will receive notifications while the app is open.');
        
        if ('Notification' in window) {
          new Notification('Notifications enabled!', {
            body: 'You will receive notifications while the app is open.',
            icon: '/src/logo.png',
          });
        }
        return true;
      }
      
      throw new Error('Failed to get FCM token');
    }

    console.log('[DEBUG] FCM token obtained:', fcmToken);

    // Send token and preferences to your backend
    console.log('[DEBUG] Sending token to backend...');
    const success = await sendPreferencesToBackend(fcmToken, preferences || {
      enabled: true,
      showScheduleReminders: true,
      showNewShowAlerts: true,
      favoriteShows: [],
      reminderTime: 15
    });

    if (success) {
      console.log('[DEBUG] Push notifications enabled successfully');
      return true;
    } else {
      // Even if backend fails, allow local notifications
      console.warn('[DEBUG] Backend failed, but enabling local notifications');
      alert('Notifications enabled! You will receive notifications while the app is open. Remote notifications may not work.');
      return true;
    }
  } catch (error: any) {
    console.error('[DEBUG] Error enabling push notifications:', error);
    console.error('[DEBUG] Error details:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack
    });
    throw error;
  }
}

export function useNotifications(scheduleData?: any[]) {
  const [settings, setSettings] = useState<NotificationSettings>({
    enabled: false,
    showScheduleReminders: true,
    showNewShowAlerts: true,
    favoriteShows: [],
    reminderTime: 15, // 15 minutes before
  });

  const [permission, setPermission] = useState<NotificationPermission>('default');
  const notificationTimeoutsRef = useRef<Map<string, number>>(new Map());

  // Set up FCM message listener
  useEffect(() => {
    if (permission === 'granted') {
      if (messaging) {
        const unsubscribe = onMessage(messaging, (payload) => {
          console.log('Received foreground message:', payload);
          
          // Show notification
          if (payload.notification) {
            new Notification(payload.notification.title || 'PlayFm Dublin', {
              body: payload.notification.body,
              icon: '/src/logo.png',
              badge: '/src/logo.png',
              data: {
                dateOfArrival: Date.now(),
                primaryKey: payload.data?.primaryKey || 1
              }
            });
          }
        });
        return () => unsubscribe();
      } else {
        console.warn('Firebase Messaging is not supported in this browser. Foreground notifications will not be received.');
      }
    }
  }, [permission]);

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem('playfm_notification_settings');
      if (saved) {
        setSettings(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
  };

  const saveSettings = (newSettings: NotificationSettings) => {
    console.log('[DEBUG] saveSettings called with:', newSettings);
    try {
      localStorage.setItem('playfm_notification_settings', JSON.stringify(newSettings));
      setSettings(newSettings);
    } catch (error) {
      console.error('Error saving notification settings:', error);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      setPermission('granted');
      return true;
    }

    if (Notification.permission === 'denied') {
      setPermission('denied');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        // Get FCM token
        let fcmToken;
        try {
          fcmToken = await getToken(messaging, {
            vapidKey: 'BFxTvVjgQXtdbPcF2SoD1ycpGx4fx-dcVHRfutW-17x98Kw4rFdLLbiTPBUSZ0UukpxbF5mBenMF9F2_WBtXo'
          });
        } catch (tokenError: any) {
          console.error('[DEBUG] Error getting FCM token in requestPermission:', tokenError);
          
          // Check if it's the atob error
          if (tokenError.message && tokenError.message.includes('atob')) {
            console.warn('[DEBUG] atob error detected in requestPermission, enabling local notifications only');
            // For Android devices with atob issues, we can still enable local notifications
            if (Capacitor.getPlatform() === 'android') {
              console.log('[DEBUG] Android device detected, enabling local notifications only');
              // Show a notification that local notifications are enabled
              if ('Notification' in window) {
                new Notification('PlayFm Dublin', {
                  body: 'Local notifications enabled! You\'ll get alerts while the app is open.',
                  icon: '/src/logo.png'
                });
              }
              return true;
            }
          }
          
          console.error('Error getting FCM token:', tokenError);
          return false;
        }

        if (fcmToken) {
          // Send token to backend
          await sendPreferencesToBackend(fcmToken, settings);
        }

        // Test notification
        if ('Notification' in window) {
          new Notification('PlayFm Dublin', {
            body: 'Notifications enabled! You\'ll get alerts for your favorite shows.',
            icon: '/src/logo.png'
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const showNotification = (options: {
    title: string;
    message: string;
    icon?: string;
    tag?: string;
    onClick?: () => void;
  }) => {
    if (Notification.permission !== 'granted' || !settings.enabled || !('Notification' in window)) return;

    try {
      // iOS Safari specific handling
      if (isSafariIOS()) {
        // On iOS, when app is open, show an in-app alert instead of notification
        if (document.hasFocus()) {
          // Show a custom in-app notification
          showInAppNotification(options);
          return;
        }
      }

      const notification = new Notification(options.title, {
        body: options.message,
        icon: options.icon || '/src/logo.png',
        tag: options.tag,
        badge: '/src/logo.png',
        requireInteraction: false,
        silent: false,
      });

      if (options.onClick) {
        notification.onclick = () => {
          options.onClick!();
          notification.close();
        };
      }

      // Auto-close after 10 seconds
      setTimeout(() => {
        notification.close();
      }, 10000);

    } catch (error) {
      console.error('Error showing notification:', error);
      // Fallback to in-app notification if browser notification fails
      showInAppNotification(options);
    }
  };

  // New function for in-app notifications (iOS fallback)
  const showInAppNotification = (options: {
    title: string;
    message: string;
    icon?: string;
    tag?: string;
    onClick?: () => void;
  }) => {
    // Create a custom notification element
    const notificationEl = document.createElement('div');
    notificationEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff0060;
      color: white;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      max-width: 300px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    
    notificationEl.innerHTML = `
      <div style="font-weight: 600; margin-bottom: 4px;">${options.title}</div>
      <div style="font-size: 14px; opacity: 0.9;">${options.message}</div>
    `;

    document.body.appendChild(notificationEl);

    // Animate in
    setTimeout(() => {
      notificationEl.style.transform = 'translateX(0)';
    }, 100);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notificationEl.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (document.body.contains(notificationEl)) {
          document.body.removeChild(notificationEl);
        }
      }, 300);
    }, 5000);

    // Click handler
    notificationEl.addEventListener('click', () => {
      if (options.onClick) {
        options.onClick();
      }
      notificationEl.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (document.body.contains(notificationEl)) {
          document.body.removeChild(notificationEl);
        }
      }, 300);
    });
  };

  const scheduleShowReminder = (show: {
    artist: string;
    showName: string;
    time: string;
    day: string;
  }) => {
    if (!settings.enabled || !settings.showScheduleReminders) return;

    const showId = `${show.artist}-${show.showName || 'Show'}`;
    if (!settings.favoriteShows.includes(showId)) return; // Only notify for favorites

    // Parse show time and calculate notification time
    const now = new Date();
    const [timeStr] = show.time.split(' - ');
    const [time, period] = timeStr.split(/(?=[ap]m)/i);
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period?.toLowerCase() === 'pm' && hours !== 12) hours += 12;
    if (period?.toLowerCase() === 'am' && hours === 12) hours = 0;

    // Find next occurrence of this show
    const showDate = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const targetDay = dayNames.indexOf(show.day);
    const currentDay = showDate.getDay();
    
    let daysUntilShow = targetDay - currentDay;
    if (daysUntilShow < 0 || (daysUntilShow === 0 && (hours < now.getHours() || (hours === now.getHours() && minutes <= now.getMinutes())))) {
      daysUntilShow += 7;
    }
    
    showDate.setDate(showDate.getDate() + daysUntilShow);
    showDate.setHours(hours, minutes, 0, 0);
    
    // Schedule notification for reminderTime minutes before
    const notificationTime = new Date(showDate.getTime() - (settings.reminderTime * 60 * 1000));
    const timeUntilNotification = notificationTime.getTime() - now.getTime();
    
    if (timeUntilNotification > 0) {
      const notificationId = `${show.artist}-${show.day}-${show.time}`;
      
      // Clear existing timeout for this show
      const existingTimeout = notificationTimeoutsRef.current.get(notificationId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      
      // Schedule new notification
      const timeoutId = window.setTimeout(() => {
        showNotification({
          title: `${show.artist} starting soon!`,
          message: `${show.showName || 'Show'} starts in ${settings.reminderTime} minutes on PlayFm Dublin`,
          tag: notificationId,
          onClick: () => {
            // Focus the app window if possible
            if (window.focus) window.focus();
          },
        });
        
        notificationTimeoutsRef.current.delete(notificationId);
      }, timeUntilNotification);
      
      notificationTimeoutsRef.current.set(notificationId, timeoutId);
    }
  };

  const addFavoriteShow = (showId: string) => {
    if (settings.favoriteShows.includes(showId)) return;
    const showDetails = getFavoriteShowDetails(showId);
    trackShowFavorited(showId, `${showDetails.artist} - ${showDetails.showName}`);
    const newFavorites = [...settings.favoriteShows, showId];
    saveSettings({ ...settings, favoriteShows: newFavorites });
  };

  const removeFavoriteShow = (showId: string) => {
    const showDetails = getFavoriteShowDetails(showId);
    trackShowUnfavorited(showId, `${showDetails.artist} - ${showDetails.showName}`);
    const newFavorites = settings.favoriteShows.filter(id => id !== showId);
    saveSettings({ ...settings, favoriteShows: newFavorites });
  };

  const toggleFavoriteShow = (showId: string) => {
    if (isFavoriteShow(showId)) {
      removeFavoriteShow(showId);
    } else {
      addFavoriteShow(showId);
    }
  };

  const isFavoriteShow = (showId: string): boolean => {
    return settings.favoriteShows.includes(showId);
  };

  const getFavoriteShowDetails = (showId: string): ShowDetails => {
    // Parse the showId to extract artist and show name
    const parts = showId.split('-');
    if (parts.length >= 2) {
      const artist = parts[0];
      const showName = parts.slice(1).join('-');
      return {
        artist,
        showName: showName === 'Show' ? '' : showName
      };
    }
    
    // Fallback if parsing fails
    return {
      artist: showId,
      showName: ''
    };
  };

  const updateSettings = (newSettings: Partial<NotificationSettings>) => {
    console.log('[DEBUG] updateSettings called with:', newSettings);
    const updated = { ...settings, ...newSettings };
    saveSettings(updated);
    // Send updated preferences and subscription to backend if enabled
    if (updated.enabled) {
      navigator.serviceWorker.ready.then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await sendPreferencesToBackend(subscription.endpoint, updated);
        }
      });
    }
  };

  const clearAllScheduledNotifications = () => {
    notificationTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    notificationTimeoutsRef.current.clear();
  };

  // Initialize
  useEffect(() => {
    loadSettings();
    
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    return () => {
      clearAllScheduledNotifications();
    };
  }, []);

  // Schedule reminders for all favorite shows when settings or scheduleData change
  useEffect(() => {
    if (
      settings.enabled &&
      settings.showScheduleReminders &&
      settings.favoriteShows.length > 0 &&
      scheduleData
    ) {
      clearAllScheduledNotifications();
      settings.favoriteShows.forEach(showId => {
        for (const day of scheduleData) {
          const show = day.shows.find(
            (slot: any) => `${slot.artist}-${slot.show || 'Show'}` === showId
          );
          if (show) {
            scheduleShowReminder({
              artist: show.artist,
              showName: show.show,
              time: show.time,
              day: day.day,
            });
            break;
          }
        }
      });
    }
  }, [settings.enabled, settings.showScheduleReminders, settings.favoriteShows, settings.reminderTime, scheduleData]);

  return {
    settings,
    permission,
    requestPermission,
    showNotification,
    scheduleShowReminder,
    addFavoriteShow,
    removeFavoriteShow,
    toggleFavoriteShow,
    isFavoriteShow,
    getFavoriteShowDetails,
    updateSettings,
    clearAllScheduledNotifications,
    getBrowserInstructions,
    // Add test function for debugging
    testNotification: () => {
      showNotification({
        title: 'Test Notification',
        message: 'This is a test notification to verify iOS compatibility',
        onClick: () => console.log('Test notification clicked')
      });
    },
  };
}

// Helper function to check if browser is Safari on iOS
function isSafariIOS(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/Firefox/.test(ua) && !/Edge/.test(ua);
  const isStandalone = window.navigator.standalone === true;
  
  // Check for iOS Safari or PWA mode on iOS
  return (isIOS && isSafari) || (isIOS && isStandalone);
}

// Helper function to get browser-specific instructions
function getBrowserInstructions(): string {
  const userAgent = navigator.userAgent.toLowerCase();
  const isAndroid = /android/i.test(navigator.userAgent);
  
  if (userAgent.includes('chrome')) {
    if (isAndroid) {
      return `
        To enable notifications in Chrome on Android:
        1. Tap the three dots (⋮) in the top-right corner
        2. Tap "Settings"
        3. Tap "Site settings"
        4. Tap "Notifications"
        5. Find this site and tap "Allow"
        6. Refresh the page and try again
        
        If notifications still don't work, try:
        - Clear browser cache and cookies
        - Update Chrome to the latest version
        - Local notifications will still work while the app is open
      `;
    } else {
      return `
        To enable notifications in Chrome:
        1. Click the three dots (⋮) in the top-right corner
        2. Go to Settings
        3. Click Privacy and security
        4. Click Site Settings
        5. Click Notifications
        6. Find this site and change to "Allow"
        7. Refresh the page and try again
      `;
    }
  } else if (userAgent.includes('firefox')) {
    return `
      To enable notifications in Firefox:
      1. Click the padlock icon in the address bar
      2. Click the ">" arrow next to "Connection secure"
      3. Click "More Information"
      4. Click "Permissions"
      5. Find "Send Notifications" and change to "Allow"
      6. Refresh the page and try again
    `;
  } else if (userAgent.includes('safari')) {
    return `
      To enable notifications in Safari:
      1. Click Safari in the top menu
      2. Click Settings
      3. Click Websites
      4. Click Notifications
      5. Find this site and change to "Allow"
      6. Refresh the page and try again
    `;
  } else {
    return `
      To enable notifications:
      1. Open your browser settings
      2. Look for "Notifications" or "Site Settings"
      3. Find this site and allow notifications
      4. Refresh the page and try again
      ${isAndroid ? '\n\nNote: Some Android browsers may have limited push notification support. Local notifications will still work while the app is open.' : ''}
    `;
  }
}